import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '800px',
    width: '90%',
    maxHeight: '90vh',
    margin: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1),
      width: '95%',
      maxHeight: '95vh',
    },
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  '&:first-of-type': {
    marginTop: 0,
  },
}));

const InfoRow = ({ label, value }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 1 }}>
      <Grid item xs={isMobile ? 12 : 4}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'medium', 
            color: 'text.secondary',
            ...(isMobile && { fontSize: '0.875rem' })
          }}
        >
          {label}:
        </Typography>
      </Grid>
      <Grid item xs={isMobile ? 12 : 8}>
        <Typography 
          variant="body2"
          sx={{
            ...(isMobile && { 
              fontSize: '0.875rem',
              marginTop: '-4px',
              marginBottom: '8px'
            })
          }}
        >
          {value || 'N/A'}
        </Typography>
      </Grid>
    </Grid>
  );
};

const InfoCard = ({ title, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2, 
            color: 'primary.main', 
            fontWeight: 'bold',
            fontSize: isMobile ? '1rem' : '1.25rem'
          }}
        >
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
};

const ProxyFormModal = ({ open, onClose, proxyForm }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (!proxyForm) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <StyledDialog open={open} onClose={onClose} scroll="paper">
      <DialogTitle sx={{ pb: isMobile ? 1 : 2 }}>
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          component="div"
          sx={{ fontSize: isMobile ? '1.1rem' : '1.5rem' }}
        >
          Proxy Form Details
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
        >
          Submitted: {formatDateTime(proxyForm.createdAt)}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers sx={{ px: isMobile ? 2 : 3, py: isMobile ? 2 : 3 }}>
        {/* Meeting Information */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Meeting Information
        </SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow label="Meeting Date" value={formatDate(proxyForm.meeting?.date)} />
          <InfoRow label="Location" value={proxyForm.meeting?.location} />
        </Box>

        <Divider />

        {/* Member Information */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Member Information
        </SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow label="Name" value={proxyForm.member?.name} />
          <InfoRow label="Membership ID" value={proxyForm.member?.id} />
          <InfoRow label="Phone" value={proxyForm.member?.phone} />
          <InfoRow label="Email" value={proxyForm.member?.email} />
        </Box>

        <Divider />

        {/* Proxy Representative */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Proxy Representative
        </SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow label="Name" value={proxyForm.proxy?.name} />
          <InfoRow label="Membership ID" value={proxyForm.proxy?.id} />
          <InfoRow label="Phone" value={proxyForm.proxy?.phone} />
        </Box>

        <Divider />

        {/* Authorization Section */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Member Authorization
        </SectionTitle>
        <InfoCard title="Authorization Details">
          <InfoRow label="Member Signature" value={proxyForm.authorization?.member_signature} />
          <InfoRow label="Printed Name" value={proxyForm.authorization?.member_printed_name} />
          <InfoRow label="Authorization Date" value={formatDate(proxyForm.authorization?.date)} />
        </InfoCard>

        {/* Proxy Acceptance */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Proxy Acceptance
        </SectionTitle>
        <InfoCard title="Acceptance Details">
          <InfoRow label="Proxy Name" value={proxyForm.acceptance?.proxy_name} />
          <InfoRow label="Acceptance Date" value={formatDate(proxyForm.acceptance?.date)} />
        </InfoCard>

        {/* Form Metadata */}
        <SectionTitle 
          variant="h6"
          sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
        >
          Form Details
        </SectionTitle>
        <Box>
          <InfoRow label="Form ID" value={proxyForm._id} />
          <InfoRow label="Submitted Date" value={formatDateTime(proxyForm.createdAt)} />
          <InfoRow label="Last Updated" value={formatDateTime(proxyForm.updatedAt)} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: isMobile ? 2 : 3, py: isMobile ? 1.5 : 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          fullWidth={isMobile}
          sx={{ 
            minWidth: isMobile ? 'auto' : '100px',
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
        >
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ProxyFormModal;
